import React, {useEffect, useRef} from 'react';

/**
 * DocklineFX — shared scroll effects for the division pages.
 *
 * Renders a "dockline": an SVG line in the division color that draws itself
 * down the page as the visitor scrolls, with glowing nodes that light up as
 * the line reaches each section. Also wires up staggered section reveals and
 * a subtle 3D tilt on cards. No particles — the landing page keeps that crown.
 *
 * Must be rendered as a direct child of the page's `.content` element.
 */

interface Props {
    /** Division accent color, e.g. "#1BA27A" */
    color: string;
}

const STAGGER_SELECTORS = [
    '.systems-grid__cards',
    '.process-steps',
    '.devs-work__cards',
    '.studios-services__cards',
    '.systems-why__grid',
    '.devs-ref__grid',
    '.studios-overview__grid',
    '.studios-tools__grid',
    '.faq-list'
].join(', ');

const TILT_SELECTOR = '.systems-card, .devs-card, .studios-card, .process-step, .media-frame';

const DocklineFX: React.FC<Props> = ({color}) => {
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const pathRef = useRef<SVGPathElement | null>(null);
    const nodesRef = useRef<SVGGElement | null>(null);

    useEffect(() => {
        const wrap = wrapRef.current;
        const svg = svgRef.current;
        const path = pathRef.current;
        const nodesG = nodesRef.current;
        const content = wrap?.parentElement as HTMLElement | null;
        if (!wrap || !svg || !path || !nodesG || !content) return;

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        /* --- 1) Section reveals with stagger --- */
        const sections = Array.from(content.querySelectorAll(':scope > section')) as HTMLElement[];
        if (!reduced) {
            sections.forEach(s => s.classList.add('fx-section'));
            content.querySelectorAll(STAGGER_SELECTORS).forEach(el => el.classList.add('fx-stagger'));
        }
        const io = new IntersectionObserver(
            entries => entries.forEach(en => {
                if (en.isIntersecting) en.target.classList.add('fx-in');
            }),
            {threshold: 0.15}
        );
        sections.forEach(s => io.observe(s));

        /* --- 2) The dockline thread --- */
        let len = 0;
        let nodeYs: number[] = [];

        const build = () => {
            const h = content.scrollHeight;
            svg.setAttribute('viewBox', `0 0 120 ${h}`);
            svg.style.height = `${h}px`;

            // Gentle weave down the page
            let d = 'M 60 0';
            const seg = 460;
            let side = 1;
            for (let y = seg; y <= h + seg; y += seg) {
                const yy = Math.min(y, h);
                d += ` Q ${60 + 36 * side} ${yy - seg / 2}, 60 ${yy}`;
                side *= -1;
            }
            path.setAttribute('d', d);
            len = path.getTotalLength();
            path.style.strokeDasharray = `${len}`;
            path.style.strokeDashoffset = `${len}`;

            // One node per section
            nodesG.innerHTML = '';
            nodeYs = [];
            const cTop = content.getBoundingClientRect().top + window.scrollY;
            sections.forEach(s => {
                const y = s.getBoundingClientRect().top + window.scrollY - cTop + 60;
                nodeYs.push(y);
                const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                c.setAttribute('cx', '60');
                c.setAttribute('cy', String(y));
                c.setAttribute('r', '5');
                c.setAttribute('class', 'fx-node');
                nodesG.appendChild(c);
            });
        };

        const onScroll = () => {
            const rect = content.getBoundingClientRect();
            const vh = window.innerHeight || 1;
            const p = Math.min(1, Math.max(0, (vh * 0.65 - rect.top) / Math.max(1, rect.height)));
            path.style.strokeDashoffset = String(len * (1 - p));
            const tipY = p * rect.height;
            const nodes = nodesG.querySelectorAll('circle');
            nodes.forEach((c, i) => {
                c.classList.toggle('fx-node--lit', tipY >= (nodeYs[i] || 0));
            });
        };

        let rebuildTimer = 0;
        const rebuild = () => {
            window.clearTimeout(rebuildTimer);
            rebuildTimer = window.setTimeout(() => {
                build();
                onScroll();
            }, 200);
        };

        build();
        onScroll();
        window.addEventListener('scroll', onScroll, {passive: true});
        window.addEventListener('resize', rebuild);
        // Content height changes as images load
        const ro = new ResizeObserver(rebuild);
        ro.observe(content);

        /* --- 3) Card tilt (desktop pointers only) --- */
        const cleanups: Array<() => void> = [];
        if (!reduced && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
            const cards = Array.from(content.querySelectorAll(TILT_SELECTOR)) as HTMLElement[];
            cards.forEach(card => {
                card.classList.add('fx-tilt');
                const move = (e: PointerEvent) => {
                    const r = card.getBoundingClientRect();
                    const rx = ((e.clientY - r.top) / Math.max(1, r.height) - 0.5) * -7;
                    const ry = ((e.clientX - r.left) / Math.max(1, r.width) - 0.5) * 7;
                    card.style.transform =
                        `perspective(900px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) translateY(-4px)`;
                };
                const leave = () => {
                    card.style.transform = '';
                };
                card.addEventListener('pointermove', move);
                card.addEventListener('pointerleave', leave);
                cleanups.push(() => {
                    card.removeEventListener('pointermove', move);
                    card.removeEventListener('pointerleave', leave);
                    card.style.transform = '';
                });
            });
        }

        return () => {
            io.disconnect();
            ro.disconnect();
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', rebuild);
            window.clearTimeout(rebuildTimer);
            cleanups.forEach(fn => fn());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="fx-thread" ref={wrapRef} aria-hidden="true" style={{color}}>
            <svg ref={svgRef} width="120" xmlns="http://www.w3.org/2000/svg">
                <path
                    ref={pathRef}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    opacity="0.85"
                />
                <g ref={nodesRef} fill="none" stroke="currentColor" strokeWidth="2"/>
            </svg>
        </div>
    );
};

export default DocklineFX;
